import { X, CheckCircle, Search, Info } from 'lucide-react';
import { useTheme } from '../../zustand/ThemeStore';
import React, { useState } from 'react';


export interface UserType {
  uid: string;
  name: string;
  email: string;
  role: string;
  status: 'suspended' | 'unsuspended';
  designation: string;
  created_at?: string;
  head?: string;
  email_verified?: boolean;
  avatar?: string;
  color?: string;
}

interface UserSelectorProps {
  users: UserType[];
  selectedUser: UserType | null;
  onSelect: (user: UserType) => void;
  onDeselect: () => void;
  userSearch: string;
  setUserSearch: (s: string) => void;
}

const UserSelector: React.FC<UserSelectorProps> = ({ users, selectedUser, onSelect, onDeselect, userSearch, setUserSearch }) => {
  const isDarkMode = useTheme((set)=> set.theme);
  const [showInfoTooltip, setShowInfoTooltip] = useState(false);
  
  const filteredUsers = users.filter((user: UserType) =>
    user.name.toLowerCase().includes(userSearch.toLowerCase()) ||
    user.role.toLowerCase().includes(userSearch.toLowerCase())
  );
  
  return (
    <div className={`rounded-2xl p-5 flex h-full flex-col border ${
      isDarkMode 
        ? 'bg-[#0a1414] border-[#1a2424]' 
        : 'bg-white border-gray-200'
    }`}>
      <div className="flex items-center justify-between mb-4 flex-shrink-0">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <h2 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Select User</h2>
            {/* Info Icon with Tooltip */}
            <div className="relative">
              <button
                onMouseEnter={() => setShowInfoTooltip(true)}
                onMouseLeave={() => setShowInfoTooltip(false)}
                onClick={() => setShowInfoTooltip(!showInfoTooltip)}
                className={`p-1 rounded-full transition-all ${
                  isDarkMode 
                    ? 'hover:bg-[#1a2424] text-gray-400 hover:text-gray-300' 
                    : 'hover:bg-gray-100 text-gray-500 hover:text-gray-600'
                }`}
              >
                <Info className="w-4 h-4" />
              </button>
              
              {/* Tooltip */}
              {showInfoTooltip && (
                <div className={`absolute left-0 top-8 z-50 w-80 p-4 rounded-xl shadow-2xl border ${
                  isDarkMode 
                    ? 'bg-[#0f1919] border-[#1a2424] text-gray-200' 
                    : 'bg-white border-gray-200 text-gray-700'
                }`}>
                  <div className="mb-3">
                    <h3 className={`text-sm font-semibold mb-2 flex items-center gap-2 ${
                      isDarkMode ? 'text-white' : 'text-gray-900'
                    }`}>
                      <span className="text-base">ℹ️</span>
                      Step 1 – "Select User"
                    </h3>
                    <p className={`text-xs mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      Before uploading calls, please select the correct user (sales rep):
                    </p>
                  </div>
                  
                  <ul className={`space-y-2 text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    <li className="flex items-start gap-2">
                      <span className="text-green-500 mt-0.5">•</span>
                      <span>If you're uploading <strong className={isDarkMode ? 'text-white' : 'text-gray-900'}>John's</strong> sales call, select <strong className={isDarkMode ? 'text-white' : 'text-gray-900'}>John</strong> as the user — not anyone else.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-500 mt-0.5">•</span>
                      <span>If you're uploading <strong className={isDarkMode ? 'text-white' : 'text-gray-900'}>Jane's</strong> sales call, select <strong className={isDarkMode ? 'text-white' : 'text-gray-900'}>Jane</strong> as the user — not anyone else.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-500 mt-0.5">•</span>
                      <span>If you're uploading <strong className={isDarkMode ? 'text-white' : 'text-gray-900'}>your own</strong> sales call, select <strong className={isDarkMode ? 'text-white' : 'text-gray-900'}>yourself</strong> as the user.</span>
                    </li>
                  </ul>
                  
                  <div className={`mt-3 pt-3 border-t text-xs ${
                    isDarkMode ? 'border-[#1a2424] text-yellow-400' : 'border-gray-200 text-yellow-600'
                  }`}>
                    <div className="flex items-start gap-2">
                      <span className="font-bold">⚠️</span>
                      <span><strong>Note:</strong> When uploading multiple audio files, make sure all calls belong to the same user (e.g., all John's or all Jane's calls).</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Choose a user before uploading files
          </p>
        </div>
        {selectedUser && (
          <button
            onClick={onDeselect}
            className={`text-xs px-3 py-1.5 rounded-full transition-all flex items-center gap-1 ${
              isDarkMode 
                ? 'bg-[#050a0a] hover:bg-[#0f1919] text-white' 
                : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
            }`}
          >
            <X className={`w-3 h-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
            Deselect
          </button>
        )}
      </div>
      {/* Search Bar */}
      <div className="relative mb-4 flex-shrink-0">
        <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${
          isDarkMode ? 'text-gray-400' : 'text-gray-500'
        }`} />
        <input
          type="text"
          placeholder="Search users..."
          value={userSearch}
          onChange={(e) => setUserSearch(e.target.value)}
          className={`w-full pl-10 pr-4 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1a9f6b] ${
            isDarkMode 
              ? 'bg-[#050a0a] text-white placeholder-gray-500' 
              : 'bg-gray-100 text-gray-900 placeholder-gray-400'
          }`}
        />
      </div>
      {/* Selected User Display */}
      <div className='overflow-hidden'>
        {selectedUser && (
          <div className={
            `mb-4 p-4 rounded-xl flex-shrink-0 ` +
            (isDarkMode
              ? 'bg-gradient-to-br from-[#183a2a] via-[#1a4d3a] to-[#224c3a] shadow-lg shadow-[#183a2a]/30'
              : 'bg-gradient-to-br from-[#183a2a] via-[#1a4d3a] to-[#224c3a] shadow-md shadow-[#b2dfd6]/20 border border-[#d6ebe2]')
          }>
            <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full flex items-center justify-center bg-gradient-to-br from-green-600 to-green-400 text-white text-lg font-bold">
                  {selectedUser.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                </div>
              <div>
                <p className="font-semibold text-white">{selectedUser.name}</p>
                <p className="text-xs text-white/80">{selectedUser.role}</p>
              </div>
              <CheckCircle className="w-5 h-5 ml-auto text-white" />
            </div>
          </div>
        )}
        {/* Users Grid */}
        <div className={`overflow-y-scroll h-full ${selectedUser? "pb-[7rem]": ""} pr-2 scrollbar-thin scrollbar-track-transparent ${
          isDarkMode ? 'scrollbar-thumb-gray-700' : 'scrollbar-thumb-gray-300'
        }`}>
          <div className="grid grid-cols-2 gap-3">
            {filteredUsers.map(user => (
              <button
                key={user.uid}
                onClick={() => onSelect(user)}
                disabled={selectedUser?.uid === user.uid}
                className={`p-4 rounded-xl transition-all ${
                  selectedUser?.uid === user.uid
                    ? `${isDarkMode ? 'bg-[#050a0a]' : 'bg-gray-200'} opacity-50 cursor-not-allowed`
                    : `${isDarkMode ? 'bg-[#050a0a] hover:bg-[#0f1919]' : 'bg-gray-100 hover:bg-gray-200'} hover:scale-105`
                }`}
              >
                  <div className="w-12 h-12 mx-auto mb-2 rounded-full flex items-center justify-center bg-gradient-to-br from-green-600 to-green-400 text-white text-lg font-bold">
                    {user.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                  </div>
                <p className={`font-medium text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{user.name}</p>
                <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{user.designation}</p>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserSelector;
